import React, { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Crown, LayoutDashboard, CalendarDays, Users, FolderKanban, DollarSign,
  Radar, AlertTriangle, Sunrise, Plug, Search, Plus, LogOut, Menu, X, Zap,
  ClipboardCheck, Globe, Sparkles, Brain, ShieldCheck, BarChart3,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/caesar";
import QuickCapture from "@/components/QuickCapture";
import AskCaesar from "@/components/AskCaesar";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/", label: "Command", icon: LayoutDashboard, end: true },
  { to: "/chief", label: "AI Chief of Staff", icon: Brain },
  { to: "/today", label: "Today", icon: Zap },
  { to: "/eod", label: "EOD Report", icon: ClipboardCheck },
  { to: "/projects", label: "Projects", icon: FolderKanban },
  { to: "/people", label: "People", icon: Users },
  { to: "/money", label: "Money", icon: DollarSign },
  { to: "/websites", label: "Websites", icon: Globe },
  { to: "/social", label: "Social", icon: Radar },
  { to: "/social-report", label: "Social Report", icon: BarChart3 },
  { to: "/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/intelligence", label: "Intelligence", icon: Sparkles },
  { to: "/alerts", label: "Red Alerts", icon: AlertTriangle },
  { to: "/brief", label: "Morning Brief", icon: Sunrise },
  { to: "/connections", label: "Connections", icon: Plug },
];

const MOBILE_NAV = [
  { to: "/", label: "Home", icon: LayoutDashboard, end: true },
  { to: "/today", label: "Today", icon: Zap },
  { to: "/people", label: "People", icon: Users },
  { to: "/projects", label: "Projects", icon: FolderKanban },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [captureOpen, setCaptureOpen] = useState(false);
  const [askOpen, setAskOpen] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const [alertCount, setAlertCount] = useState(0);
  const [now, setNow] = useState(new Date());

  useEffect(() => { const t = setInterval(() => setNow(new Date()), 30000); return () => clearInterval(t); }, []);
  useEffect(() => { setDrawer(false); }, [location.pathname]);
  useEffect(() => {
    let active = true;
    api.get("/alerts").then(({ data }) => active && setAlertCount((data || []).filter((a) => a.status === "active").length)).catch(() => {});
    return () => { active = false; };
  }, [location.pathname]);

  const vivianItem = user?.role === "team_member"
    ? { to: "/vivian", label: "My Command", icon: Crown }
    : { to: "/vivian", label: "Vivian Command", icon: Crown };

  let navItems;
  if (user?.role === "member_investor") {
    navItems = [{ to: "/portal", label: "Member Portal", icon: Crown }];
  } else if (user?.role === "team_member") {
    navItems = [vivianItem, ...NAV.filter((n) => n.to !== "/")];
  } else {
    navItems = [...NAV, { to: "/admin", label: "Admin", icon: ShieldCheck }];
  }

  const SidebarLink = ({ item }) => {
    const Icon = item.icon;
    return (
      <NavLink
        to={item.to}
        end={item.end}
        data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
        className={({ isActive }) => cn(
          "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 relative",
          isActive ? "bg-surface2 text-gold" : "text-[#A3A3A3] hover:text-[#FAFAFA] hover:bg-surface"
        )}
      >
        <Icon size={18} />
        <span>{item.label}</span>
        {item.to === "/alerts" && alertCount > 0 && (
          <span className="ml-auto font-num text-xs bg-sred text-white rounded-full px-1.5 py-0.5">{alertCount}</span>
        )}
      </NavLink>
    );
  };

  return (
    <div className="min-h-screen bg-ink text-[#FAFAFA]">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col fixed inset-y-0 left-0 w-64 border-r border-edge bg-ink z-30">
        <div className="px-5 py-6 flex items-center gap-3 border-b border-edge">
          <div className="w-9 h-9 rounded-lg bg-surface border border-gold/40 grid place-items-center overflow-hidden">
            <img src="/szr-logo.png" alt="SZR" className="w-full h-full object-contain" />
          </div>
          <div>
            <div className="font-display font-extrabold tracking-tight leading-none">SZR</div>
            <div className="label-caps text-[0.55rem] text-gold mt-0.5">Command Center</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {navItems.map((item) => <SidebarLink key={item.to + item.label} item={item} />)}
        </nav>
        <div className="p-3 border-t border-edge space-y-1">
          <button data-testid="capture-btn-desktop" onClick={() => setCaptureOpen(true)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg bg-gold text-ink text-sm font-bold hover:bg-[#e6c14e] transition-colors duration-150">
            <Plus size={18} /> Capture
          </button>
          <button data-testid="ask-btn-desktop" onClick={() => setAskOpen(true)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-[#A3A3A3] hover:text-[#FAFAFA] hover:bg-surface transition-colors duration-150">
            <Search size={18} /> Ask SZR
          </button>
          <div className="flex items-center gap-3 px-3 pt-3">
            <div className="w-8 h-8 rounded-full bg-surface2 border border-edge grid place-items-center overflow-hidden">
              {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : <span className="text-xs font-bold">{user?.name?.[0]}</span>}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="label-caps text-[0.55rem]" style={{ color: user?.role === "super_admin" ? "#D4AF37" : "#A3A3A3" }}>{user?.role}</div>
            </div>
            <button data-testid="logout-btn" onClick={logout} className="text-[#A3A3A3] hover:text-sred transition-colors"><LogOut size={16} /></button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 bg-ink/95 backdrop-blur border-b border-edge px-4 h-14 flex items-center justify-between">
        <button data-testid="mobile-menu-btn" onClick={() => setDrawer(true)} className="p-1"><Menu size={22} /></button>
        <div className="flex items-center gap-2">
          <img src="/szr-logo.png" alt="SZR" className="w-6 h-6 object-contain" />
          <span className="font-display font-extrabold tracking-tight text-sm">SZR COMMAND CENTER</span>
        </div>
        <button data-testid="ask-btn-mobile" onClick={() => setAskOpen(true)} className="p-1"><Search size={20} /></button>
      </header>

      {/* Mobile drawer */}
      {drawer && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/70" onClick={() => setDrawer(false)} />
          <div className="absolute inset-y-0 left-0 w-72 bg-ink border-r border-edge p-4 overflow-y-auto animate-fade-up">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2"><img src="/szr-logo.png" alt="SZR" className="w-6 h-6 object-contain" /><span className="font-display font-extrabold">SZR COMMAND</span></div>
              <button onClick={() => setDrawer(false)}><X size={20} /></button>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-surface border border-edge mb-4">
              <div className="w-9 h-9 rounded-full bg-surface2 grid place-items-center overflow-hidden">
                {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : <span className="font-bold">{user?.name?.[0]}</span>}
              </div>
              <div className="flex-1"><div className="font-semibold text-sm">{user?.name}</div><div className="label-caps text-[0.55rem] text-gold">{user?.role}</div></div>
              <button onClick={logout} className="text-[#A3A3A3]"><LogOut size={18} /></button>
            </div>
            <nav className="space-y-1">
              {navItems.map((item) => <SidebarLink key={"m" + item.to + item.label} item={item} />)}
            </nav>
          </div>
        </div>
      )}

      {/* Main */}
      <main className="lg:pl-64 pb-24 lg:pb-0">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">{children}</div>
      </main>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-ink/95 backdrop-blur border-t border-edge h-16 grid grid-cols-5 items-center px-2">
        {MOBILE_NAV.slice(0, 2).map((item) => <BottomLink key={item.to} item={item} />)}
        <div className="flex justify-center">
          <button data-testid="capture-btn-mobile" onClick={() => setCaptureOpen(true)} className="w-14 h-14 -mt-6 rounded-full bg-gold text-ink grid place-items-center shadow-lg shadow-gold/20 border-4 border-ink">
            <Plus size={26} />
          </button>
        </div>
        {MOBILE_NAV.slice(2).map((item) => <BottomLink key={item.to} item={item} />)}
      </nav>

      <QuickCapture open={captureOpen} onOpenChange={setCaptureOpen} />
      <AskCaesar open={askOpen} onOpenChange={setAskOpen} />
    </div>
  );
}

function BottomLink({ item }) {
  const Icon = item.icon;
  return (
    <NavLink to={item.to} end={item.end}
      data-testid={`mobilenav-${item.label.toLowerCase()}`}
      className={({ isActive }) => cn("flex flex-col items-center gap-1 text-[0.6rem] font-medium", isActive ? "text-gold" : "text-[#A3A3A3]")}>
      <Icon size={20} />
      {item.label}
    </NavLink>
  );
}
